import React from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import moment from 'moment-timezone';
import {List, Map} from 'immutable';

import {pluralize} from 'lib/stringUtils';

import notifier from '@albert-io/notifier';
import {addToast, Banner, Button, Dialogue, LoadingSpinner, Text} from '@albert-io/atomic';
import {resource} from '@albert-io/json-api-framework/request/builder';

import errorCodes from 'client/errorCodes';
import CollapsibleList from 'client/components/CollapsibleList/CollapsibleList.react';
import ManageRosterContext from 'client/Classrooms/Classroom/Teacher/ManageRoster/ManageRoster.context';

import systemTimeOffsetStore from 'generic/SystemTimeOffset/SystemTimeOffset.store';

export default class BulkResendInvitations extends React.Component {
  static propTypes = {
    onCancel: PropTypes.func,
    onConfirm: PropTypes.func
  };

  constructor() {
    super();

    this.state = {
      fulfilledInvitations: List(),
      isReady: false,
      isRequestInflight: false,
      rejectedInvitations: List(),
      resendableInvitations: List()
    };
  }

  async componentDidMount() {
    // We use the results of this query to ascertain the total number of classroom invitations.
    // This is necessary because we can't assume this query will have resolved in every instance
    // we are using this context object.
    await this.context.queries.invitations.getResourcePromise();

    // This query isolates only the invitations we can re-send according to business logic
    const classroom = await this.fetchClassroomWithResendableInvitations();
    this.setState({
      isReady: true,
      resendableInvitations: classroom.getClassroomInvitations()
    });
  }

  static contextType = ManageRosterContext;

  fetchClassroomWithResendableInvitations = () => {
    const currentTime = moment(systemTimeOffsetStore.getCurrentTime());
    return (
      resource('classroom_v1')
        .mandarkEndpoint(['classrooms_v1', this.context.classroom.getId()])
        // We access the invitations as an included resource
        // on classroom to skirt the 250 limit on page size.
        .include('classroom_invitations_v1')
        .filter({
          included: {
            classroom_invitations_v1: {
              accepted_at: {null: true},
              sent_email_at: {
                less_than_inclusive: currentTime.subtract(1, 'day').valueOf()
              }
            }
          }
        })
        .getResourcePromise()
    );
  };

  resendInvitations = () => {
    this.setState({isRequestInflight: true});

    const promises = this.state.resendableInvitations.map((invitation) => {
      /**
       * The following behavior polyfills Promise.allSettled (which has incomplete
       * browser support as of 3/16/2020). We catch errors, replace them by returning a non-breaking
       * object, and parse the result. See https://medium.com/trabe/using-promise-allsettled-now-e1767d43e480
       */
      const promise = invitation.setSentEmailAt(null).save();
      return promise
        .then((response) =>
          Map({
            status: 'fulfilled',
            email: invitation.getEmail(),
            response
          })
        )
        .catch((error) =>
          Map({
            status: 'rejected',
            email: invitation.getEmail(),
            reason: error.response.body.errors[0].code
          })
        );
    });

    Promise.all(promises)
      .then((invitations) => {
        const parsedResponse = invitations.reduce(
          (acc, curr) => {
            const status = curr.get('status');
            if (status === 'fulfilled') {
              return acc.update('fulfilled', (fulfilled) => fulfilled.push(curr));
            }

            return acc.update('rejected', (rejected) => rejected.push(curr));
          },
          Map({
            fulfilled: List(),
            rejected: List()
          })
        );

        return parsedResponse;
      })
      .then((parsedResponse) => {
        this.setState(() => ({
          fulfilledInvitations: parsedResponse.get('fulfilled'),
          rejectedInvitations: parsedResponse.get('rejected'),
          isRequestInflight: false
        }));
      })
      .catch((error) => {
        addToast({
          title: 'Something went wrong',
          type: 'danger',
          message: 'There was a problem updating your roster. If the problem persists, contact us.'
        });
        notifier.notify(error, {
          name: 'Error encountered during bulk re-invite',
          component: 'BulkResendInvitations'
        });
      });
  };

  render() {
    const {onConfirm, onCancel, ...rest} = this.props;
    const {
      fulfilledInvitations,
      rejectedInvitations,
      resendableInvitations,
      isReady,
      isRequestInflight
    } = this.state;
    const showSummary =
      (!isRequestInflight && fulfilledInvitations.size !== 0) || rejectedInvitations.size !== 0;

    // Modal steps. The order here does not reflect the order in which the user advances.
    // Rather, this works as a sort of waterfall of states in which they can land.

    // Summary of completed re-invitations, sorted by successes and failures
    if (showSummary) {
      return (
        <BulkResendSummary
          fulfilledInvitations={fulfilledInvitations}
          onConfirm={onConfirm}
          rejectedInvitations={rejectedInvitations}
          {...rest}
        />
      );
    }

    // Initial loading state
    if (!isRequestInflight && !isReady) {
      return (
        <Dialogue title='Re-send invitations' handleClose={onCancel} {...rest}>
          <Dialogue.Body>
            <LoadingSpinner size={2} className='u-mar-b_4' />
          </Dialogue.Body>
        </Dialogue>
      );
    }

    // Discourage user from closing window mid-flight
    if (isRequestInflight) {
      return (
        <Dialogue handleClose={onConfirm} title='Re-send invitations' {...rest}>
          <Dialogue.Body>
            <LoadingSpinner size={2} className='u-mar-b_4' />
            <Text as='p' className='u-text-align_center u-mar-b_2'>
              We&apos;re saving your changes &mdash; please do not close this window.
            </Text>
          </Dialogue.Body>
        </Dialogue>
      );
    }

    if (!isRequestInflight && isReady && resendableInvitations.isEmpty()) {
      return <NoStaleInvitationsPrompt onCancel={onCancel} {...rest} />;
    }

    return (
      <ResendInvitationsPrompt
        resendableInvitations={resendableInvitations}
        onCancel={onCancel}
        onConfirm={this.resendInvitations}
        {...rest}
      />
    );
  }
}

const ResendInvitationsPrompt = ({resendableInvitations, onCancel, onConfirm, ...rest}) => {
  const {queries, classroom} = React.useContext(ManageRosterContext);
  const invitationsTotal = queries.invitations
    .getResourceMetadata()
    .getIn(['page', 'total_entries']);
  return (
    <Dialogue groupTitle title='Re-send invitations' handleClose={onCancel} {...rest}>
      <Dialogue.Body>
        <Text emphasis='low'>for {classroom.getName()}</Text>
        <br />
        <br />
        {resendableInvitations.size < invitationsTotal && (
          <Text as='p'>
            Some invitations were sent within 24 hours and will not be re-sent.
            <br />
            You can only re-send invitations every 24 hours.
          </Text>
        )}
        <Text as='p' className='u-mar-b_0'>
          {`Would you like to re-invite ${resendableInvitations.size} ${pluralize(
            'student',
            resendableInvitations.size
          )}?`}
        </Text>
      </Dialogue.Body>
      <Dialogue.BtnGroup>
        <Button color='secondary' onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={onConfirm}>Resend</Button>
      </Dialogue.BtnGroup>
    </Dialogue>
  );
};

ResendInvitationsPrompt.propTypes = {
  resendableInvitations: ImmutablePropTypes.list,
  onCancel: PropTypes.func,
  onConfirm: PropTypes.func
};

const NoStaleInvitationsPrompt = ({onCancel, ...rest}) => (
  <Dialogue title='Re-send invitations' handleClose={onCancel} {...rest}>
    <Dialogue.Body>
      <Text as='p'>You can only re-send invitations every 24 hours.</Text>
      <Text as='p' className='u-mar-b_0'>
        When invitations become available to re-send, they will appear here.
      </Text>
    </Dialogue.Body>
    <Dialogue.BtnGroup>
      <Button color='secondary' onClick={onCancel}>
        Close
      </Button>
    </Dialogue.BtnGroup>
  </Dialogue>
);

NoStaleInvitationsPrompt.propTypes = {
  onCancel: PropTypes.func
};

const BulkResendSummary = ({fulfilledInvitations, onConfirm, rejectedInvitations, ...rest}) => {
  const sortedRejections = React.useMemo(
    () =>
      rejectedInvitations.reduce(
        (acc, curr) => {
          if (curr.get('reason') === errorCodes.BAD_REQUEST.VALIDATION_ERROR.INVALID_EMAIL_DOMAIN) {
            return acc.update('invalidDomains', (items) => items.push(curr));
          }
          return acc.update('other', (items) => items.push(curr));
        },
        Map({
          invalidDomains: List(),
          other: List()
        })
      ),
    [rejectedInvitations]
  );

  return (
    <Dialogue title='Confirmation' handleClose={onConfirm} {...rest}>
      <Dialogue.Body>
        {fulfilledInvitations.size !== 0 && (
          <>
            <Banner className='u-mar-b_1' theme='success'>
              Invitations were re-sent to the following recipients:
            </Banner>
            <CollapsibleList
              className='u-mar-t_0 u-mar-b_1 u-pad_0'
              items={fulfilledInvitations.map((item) => item.get('email'))}
              maxVisibleItems={10}
            />
          </>
        )}
        {sortedRejections.get('invalidDomains').size !== 0 && (
          <>
            <Banner className='u-mar-t_3 u-mar-b_1' theme='danger'>
              <span>
                The following addresses belong to an email domain that is restricted by your school.{' '}
                <strong>Invitations could not be sent</strong>:
              </span>
            </Banner>
            <CollapsibleList
              className='u-mar-t_0 u-mar-b_1 u-pad_0'
              items={sortedRejections.get('invalidDomains').map((item) => item.get('email'))}
              maxVisibleItems={10}
            />
          </>
        )}
        {sortedRejections.get('other').size !== 0 && (
          <>
            <Banner className='u-mar-t_3 u-mar-b_1' theme='danger' dismissable={false}>
              We encountered a problem sending invitations to the following recipients. Please try
              again:
            </Banner>
            <CollapsibleList
              className='u-mar-t_0 u-mar-b_1 u-pad_0'
              items={sortedRejections.get('other').map((item) => item.get('email'))}
              maxVisibleItems={10}
            />
          </>
        )}
      </Dialogue.Body>
      <Dialogue.BtnGroup>
        <Button color='secondary' onClick={onConfirm}>
          Close
        </Button>
      </Dialogue.BtnGroup>
    </Dialogue>
  );
};

BulkResendSummary.propTypes = {
  fulfilledInvitations: ImmutablePropTypes.listOf(ImmutablePropTypes.map),
  onConfirm: PropTypes.func,
  rejectedInvitations: ImmutablePropTypes.list(ImmutablePropTypes.map)
};
